/**
 * CheckboxExamples.tsx
 * Comprehensive examples of HeroUI Checkbox and CheckboxGroup components
 * 
 * Showcases all HeroUI Checkbox features using our shared components
 */

import React, { useState } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Checkbox, CheckboxGroup } from '@enfomentis/ui';
import { Card, CardHeader, CardBody } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';
import { 
  Bell,
  Mail,
  Shield,
  Star,
  Heart,
  Trash2
} from 'lucide-react';

export const CheckboxExamples: React.FC = () => {
  // Individual checkbox states
  const [singleStates, setSingleStates] = useState<Record<string, boolean>>({
    basic: false,
    selected: true,
    primary: false,
    secondary: false,
    success: true,
    warning: false,
    danger: false,
    small: false,
    medium: true,
    large: false,
    disabled: false,
    indeterminate: false,
    required: false,
    readonly: true,
    lineThrough: true,
    custom: false
  });

  // CheckboxGroup states
  const [groupStates, setGroupStates] = useState<Record<string, string[]>>({
    cities: ['sydney', 'tokyo'],
    preferences: ['notifications', 'newsletter'],
    features: ['analytics'],
    permissions: ['read', 'write'],
    colors: ['primary'],
    horizontal: ['option1', 'option3'],
    controlled: ['item2'],
    validation: []
  });

  // Form validation state
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const handleSingleChange = (key: string, value: boolean) => {
    setSingleStates(prev => ({ ...prev, [key]: value }));
  };

  const handleGroupChange = (key: string, values: string[]) => {
    setGroupStates(prev => ({ ...prev, [key]: values }));
    
    // Clear validation error when user selects
    if (validationErrors[key] && values.length > 0) {
      setValidationErrors(prev => ({ ...prev, [key]: '' }));
    }
  };

  // Helper to safely get boolean state
  const getSingleState = (key: string): boolean => singleStates[key] ?? false;
  
  // Helper to safely get array state  
  const getGroupState = (key: string): string[] => groupStates[key] ?? [];

  const validateRequired = (key: string) => {
    const values = getGroupState(key);
    if (values.length === 0) {
      setValidationErrors(prev => ({ 
        ...prev, 
        [key]: 'At least one option must be selected' 
      }));
    }
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Header */}
        <div className="space-y-2">
          <h1 className="text-3xl font-bold text-text-primary">Checkbox Examples</h1>
          <p className="text-text-secondary">
            Comprehensive showcase of HeroUI Checkbox and CheckboxGroup components with automatic theme integration
          </p>
        </div>

        {/* Basic Checkbox Examples */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Individual Checkboxes</h2>
              <Badge color="primary" variant="flat" size="sm">Single selection</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Checkbox 
                isSelected={getSingleState('basic')}
                onValueChange={(value) => handleSingleChange('basic', value)}
                color="default"
              >
                Basic checkbox
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('selected')}
                onValueChange={(value) => handleSingleChange('selected', value)}
                color="primary"
              >
                Pre-selected
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('custom')}
                onValueChange={(value) => handleSingleChange('custom', value)}
                color="primary"
                icon={<Heart className="w-3 h-3" />}
              >
                Custom icon
              </Checkbox>
              
              <Checkbox 
                isDisabled
                color="default"
              >
                Disabled unchecked
              </Checkbox>
              
              <Checkbox 
                isDisabled
                isSelected
                color="primary"
              >
                Disabled checked
              </Checkbox>
              
              <Checkbox 
                isIndeterminate
                color="primary"
              >
                Indeterminate state
              </Checkbox>
            </div>
          </CardBody>
        </Card>

        {/* Checkbox Colors */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Checkbox Colors</h2>
              <Badge color="secondary" variant="flat" size="sm">Theme colors</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <Checkbox 
                isSelected={getSingleState('primary')}
                onValueChange={(value) => handleSingleChange('primary', value)}
                color="primary"
              >
                Primary color
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('secondary')}
                onValueChange={(value) => handleSingleChange('secondary', value)}
                color="secondary"
              >
                Secondary color
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('success')}
                onValueChange={(value) => handleSingleChange('success', value)}
                color="success"
              >
                Success color
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('warning')}
                onValueChange={(value) => handleSingleChange('warning', value)}
                color="warning"
              >
                Warning color
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('danger')}
                onValueChange={(value) => handleSingleChange('danger', value)}
                color="danger"
              >
                Danger color
              </Checkbox>
            </div>
          </CardBody>
        </Card>

        {/* Checkbox Sizes */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Checkbox Sizes</h2>
              <Badge color="warning" variant="flat" size="sm">3 sizes</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Checkbox 
                isSelected={getSingleState('small')}
                onValueChange={(value) => handleSingleChange('small', value)}
                color="primary"
                size="sm"
              >
                Small checkbox
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('medium')}
                onValueChange={(value) => handleSingleChange('medium', value)}
                color="primary"
                size="md"
              >
                Medium checkbox
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('large')}
                onValueChange={(value) => handleSingleChange('large', value)}
                color="primary"
                size="lg"
              >
                Large checkbox
              </Checkbox>
            </div>
          </CardBody>
        </Card>

        {/* Special Features */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Special Features</h2>
              <Badge color="success" variant="flat" size="sm">Advanced</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <Checkbox 
                isSelected={getSingleState('required')}
                onValueChange={(value) => handleSingleChange('required', value)}
                color="primary"
                isRequired
              >
                Required checkbox
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('readonly')}
                onValueChange={(value) => handleSingleChange('readonly', value)}
                color="secondary"
                isReadOnly
              >
                Read-only checkbox
              </Checkbox>
              
              <Checkbox 
                isSelected={getSingleState('lineThrough')}
                onValueChange={(value) => handleSingleChange('lineThrough', value)}
                color="success"
                lineThrough
              >
                Line-through when checked
              </Checkbox>
            </div>
          </CardBody>
        </Card>

        {/* CheckboxGroup - Basic */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">CheckboxGroup - Basic</h2>
              <Badge color="primary" variant="flat" size="sm">Multi-select</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <CheckboxGroup
                label="Select cities to visit"
                color="primary"
                value={getGroupState('cities')}
                onValueChange={(values) => handleGroupChange('cities', values)}
              >
                <Checkbox value="buenos-aires">Buenos Aires</Checkbox>
                <Checkbox value="sydney">Sydney</Checkbox>
                <Checkbox value="san-francisco">San Francisco</Checkbox>
                <Checkbox value="london">London</Checkbox>
                <Checkbox value="tokyo">Tokyo</Checkbox>
              </CheckboxGroup>
              
              <CheckboxGroup
                label="Notification preferences"
                description="Choose which notifications you'd like to receive"
                color="secondary"
                value={getGroupState('preferences')}
                onValueChange={(values) => handleGroupChange('preferences', values)}
              >
                <Checkbox value="notifications">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Email notifications
                  </div>
                </Checkbox>
                <Checkbox value="newsletter">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Newsletter subscription
                  </div>
                </Checkbox>
                <Checkbox value="updates">
                  <div className="flex items-center gap-2">
                    <Star className="w-4 h-4" />
                    Product updates
                  </div>
                </Checkbox>
                <Checkbox value="security">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Security alerts
                  </div>
                </Checkbox>
              </CheckboxGroup>
            </div>
          </CardBody>
        </Card>

        {/* CheckboxGroup - Colors & Orientations */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">CheckboxGroup Variants</h2>
              <Badge color="secondary" variant="flat" size="sm">Colors & layout</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-8">
            <CheckboxGroup
              label="Color variants"
              color="success"
              value={getGroupState('colors')}
              onValueChange={(values) => handleGroupChange('colors', values)}
            >
              <Checkbox value="primary" color="primary">Primary</Checkbox>
              <Checkbox value="secondary" color="secondary">Secondary</Checkbox>
              <Checkbox value="success" color="success">Success</Checkbox>
              <Checkbox value="warning" color="warning">Warning</Checkbox>
              <Checkbox value="danger" color="danger">Danger</Checkbox>
            </CheckboxGroup>
            
            <CheckboxGroup
              label="Horizontal layout"
              description="Checkboxes arranged horizontally"
              orientation="horizontal"
              color="primary"
              value={getGroupState('horizontal')}
              onValueChange={(values) => handleGroupChange('horizontal', values)}
            >
              <Checkbox value="option1">Option 1</Checkbox>
              <Checkbox value="option2">Option 2</Checkbox>
              <Checkbox value="option3">Option 3</Checkbox>
              <Checkbox value="option4">Option 4</Checkbox>
            </CheckboxGroup>
          </CardBody>
        </Card>

        {/* CheckboxGroup - States */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">CheckboxGroup States</h2>
              <Badge color="warning" variant="flat" size="sm">Interactive</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              <CheckboxGroup
                label="Disabled group"
                isDisabled
                color="primary"
                defaultValue={["feature1", "feature3"]}
              >
                <Checkbox value="feature1">Feature 1</Checkbox>
                <Checkbox value="feature2">Feature 2</Checkbox>
                <Checkbox value="feature3">Feature 3</Checkbox>
              </CheckboxGroup>
              
              <CheckboxGroup
                label="Read-only group"
                description="These options cannot be changed"
                isReadOnly
                color="secondary"
                value={["permission1", "permission2"]}
              >
                <Checkbox value="permission1">Read permission</Checkbox>
                <Checkbox value="permission2">Write permission</Checkbox>
                <Checkbox value="permission3">Delete permission</Checkbox>
              </CheckboxGroup>
            </div>
          </CardBody>
        </Card>

        {/* CheckboxGroup - Validation */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">CheckboxGroup Validation</h2>
              <Badge color="danger" variant="flat" size="sm">Required</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="max-w-md">
              <CheckboxGroup
                label="Account features"
                description="Select at least one feature to enable"
                isRequired
                color="primary"
                value={getGroupState('validation')}
                onValueChange={(values) => handleGroupChange('validation', values)}
                isInvalid={!!validationErrors['validation']}
                errorMessage={validationErrors['validation']}
              >
                <Checkbox value="analytics">Analytics Dashboard</Checkbox>
                <Checkbox value="reports">Advanced Reports</Checkbox>
                <Checkbox value="alerts">Email Alerts</Checkbox>
                <Checkbox value="api">API Access</Checkbox>
              </CheckboxGroup>
              
              <div className="mt-4">
                <Button 
                  size="sm" 
                  color="primary"
                  onPress={() => validateRequired('validation')}
                >
                  Validate Selection
                </Button>
              </div>
            </div>
          </CardBody>
        </Card>

        {/* Advanced Example */}
        <Card>
          <CardHeader>
            <div className="flex items-center gap-2">
              <h2 className="text-xl font-semibold">Advanced Example</h2>
              <Badge variant="flat" size="sm">Real-world usage</Badge>
            </div>
          </CardHeader>
          <CardBody className="space-y-6">
            <div className="max-w-2xl space-y-6">
              {/* Account Settings */}
              <CheckboxGroup
                label="Account Settings"
                description="Customize your account preferences"
                color="primary"
                value={getGroupState('controlled')}
                onValueChange={(values) => handleGroupChange('controlled', values)}
              >
                <Checkbox value="item1">
                  <div className="flex items-center gap-2">
                    <Bell className="w-4 h-4" />
                    Email Notifications
                  </div>
                </Checkbox>
                <Checkbox value="item2">
                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4" />
                    Discoverable by Email
                  </div>
                </Checkbox>
                <Checkbox value="item3">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4" />
                    Two-Factor Authentication
                  </div>
                </Checkbox>
                <Checkbox value="item4">
                  <div className="flex items-center gap-2">
                    <Trash2 className="w-4 h-4" />
                    Auto-delete Old Data
                  </div>
                </Checkbox>
              </CheckboxGroup>

              {/* Selection Summary */}
              <div className="p-4 bg-content2 rounded-lg">
                <h3 className="font-medium text-text-primary mb-2">Current Selection:</h3>
                <p className="text-sm text-text-secondary">
                  {getGroupState('controlled').length === 0 
                    ? 'No options selected'
                    : `Selected: ${getGroupState('controlled').join(', ')}`
                  }
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};